import { useEffect, useRef, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import {
  getStudiesPath,
  getSourcexPatientsPath,
  getSourcexTemplatesPath,
} from "../services/firestorePaths";
import AddPatientModal from "./AddPatientModal";
import GenerateTemplateModal from "./GenerateTemplateModal";
import { PROTOCOL_COMPARE_ENABLED } from "../config/featureFlags";
import { useSiteAdmin } from "../components/sourcex/useSiteAdmin";
import { logProtocolEvent } from "../services/protocolVersionService";
import SourceDocChat from "./SourceDocChat";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  PROTOCOL_EXTRACTION_ENABLED,
  ECRF_EXTRACTION_ENABLED,
  JOIN_REVIEW_ENABLED,
} from "../config/featureFlags";
import {
  ArrowLeft,
  Home,
  Plus,
  FileText,
  FlaskConical,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Search,
  Trash2,
  Undo2,
  Loader2,
} from "lucide-react";
import { useDialog } from "../components/sourcex/useDialog";

// Short, local date for a removal stamp. Tolerates a malformed value rather
// than throwing inside a card.
const formatStamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
};

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
  </div>
);

// The selected study is remembered for the tab so that navigating into any
// SourceX subpage (template, protocol, eCRF, join, a patient) and coming back
// lands on the same study instead of an empty selector. sessionStorage — not
// localStorage — so it survives navigation and reload but not a new tab.
const SELECTED_STUDY_KEY = "sourcex_studyId";

const readSavedStudy = () => {
  try {
    return sessionStorage.getItem(SELECTED_STUDY_KEY) || "";
  } catch {
    return ""; // sessionStorage can be unavailable in restricted contexts
  }
};

export default function SourceXHome() {
  const isSiteAdmin = useSiteAdmin();
  const [studies, setStudies] = useState([]);
  // Seeded synchronously so the selector renders the saved study on the very
  // first paint, with no flash of "Select Study".
  const [selectedStudy, setSelectedStudy] = useState(readSavedStudy);
  const [hasAssembled, setHasAssembled] = useState(false);
  // Distinguishes "still fetching" from "confirmed empty/not built" — without
  // these, the brief gap after selecting a study looked identical to a study
  // that genuinely has no patients or no built documents yet, which could
  // read as alarming (or invite a click on the wrong step to "fix" it).
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [assembledLoading, setAssembledLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [studyOpen, setStudyOpen] = useState(false);
  const [studySearch, setStudySearch] = useState("");
  const [patientPage, setPatientPage] = useState(1); // enrolled-patients pager
  // Id of the patient whose remove/restore write is in flight.
  const [pendingId, setPendingId] = useState(null);
  // Viewing the removed patients instead of the active ones.
  const [showRemoved, setShowRemoved] = useState(false);
  const { confirm: askConfirm, alert: showAlert, dialog } = useDialog();
  const studyRef = useRef(null);
  const navigate = useNavigate();

  // Removed patients are filtered out CLIENT-SIDE rather than with a
  // `where("deletedAt", "==", null)` clause: existing docs have no such field
  // at all, so a server-side equality filter would match none of them and the
  // whole list would vanish until every doc was backfilled.
  const activePatients = patients.filter((p) => !p.deletedAt);
  const removedPatients = patients.filter((p) => p.deletedAt);
  const listedPatients = showRemoved ? removedPatients : activePatients;

  // Patients per page in the enrolled-patients grid (3 cols × 5 rows).
  const PATIENTS_PER_PAGE = 15;
  const patientPageCount = Math.max(
    1,
    Math.ceil(listedPatients.length / PATIENTS_PER_PAGE)
  );
  const safePatientPage = Math.min(patientPage, patientPageCount);
  const patientStart = (safePatientPage - 1) * PATIENTS_PER_PAGE;
  const pagedPatients = listedPatients.slice(
    patientStart,
    patientStart + PATIENTS_PER_PAGE
  );

  // Keep the page in range as the list grows/shrinks or the study changes.
  useEffect(() => {
    setPatientPage((p) => Math.min(p, patientPageCount));
  }, [patientPageCount]);
  // Switching between active and removed starts at page 1 — page 3 of one list
  // rarely means anything in the other.
  useEffect(() => {
    setPatientPage(1);
  }, [showRemoved]);
  useEffect(() => {
    setPatientPage(1);
  }, [selectedStudy]);

  // Close the study dropdown on outside click.
  useEffect(() => {
    const handler = (e) => {
      if (studyRef.current && !studyRef.current.contains(e.target))
        setStudyOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ✅ Load studies
  useEffect(() => {
    const fetchStudies = async () => {
      const snap = await getDocs(collection(db, getStudiesPath()));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setStudies(data);
    };
    fetchStudies();
  }, []);

  // 🔹 Remove a patient from SourceX — a SOFT delete.
  //
  // A SourceX patient doc IS the source document: the regulated record a
  // monitor reads. Destroying it would take every recorded answer with it and
  // leave no trace that anyone did so. Instead the doc is stamped and hidden
  // from the list, which keeps both the record and the fact of its removal.
  // Nothing here ever calls deleteDoc.
  const handleRemovePatient = async (p) => {
    if (pendingId) return; // one at a time; a second click would re-run it
    const ok = await askConfirm(
      `${
        p.name || "This patient"
      } will be hidden from this study's list. The source document and every answer in it are kept, and you can restore it from “Removed”.`,
      {
        title: "Remove this patient from SourceX?",
        confirmLabel: "Remove",
        tone: "danger",
      }
    );
    if (!ok) return;

    setPendingId(p.id);
    const stamp = {
      deletedAt: new Date().toISOString(),
      deletedBy: auth.currentUser?.email || "unknown",
    };
    try {
      await updateDoc(doc(db, getSourcexPatientsPath(), p.id), stamp);
      // Patch locally rather than refetching, so the list doesn't flash.
      setPatients((prev) =>
        prev.map((x) => (x.id === p.id ? { ...x, ...stamp } : x))
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[SourceXHome] remove patient failed:", err);
      await showAlert("Sorry — the patient could not be removed. Please try again.", {
        title: "Remove failed",
        tone: "danger",
      });
    } finally {
      setPendingId(null);
    }
  };

  // 🔹 Put a removed patient back in the list. `deletedBy` is deliberately left
  // in place — who removed it and who put it back are both part of the trail.
  const handleRestorePatient = async (p) => {
    if (pendingId) return;
    setPendingId(p.id);
    const stamp = {
      deletedAt: null,
      restoredAt: new Date().toISOString(),
      restoredBy: auth.currentUser?.email || "unknown",
    };
    try {
      await updateDoc(doc(db, getSourcexPatientsPath(), p.id), stamp);
      setPatients((prev) =>
        prev.map((x) => (x.id === p.id ? { ...x, ...stamp } : x))
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[SourceXHome] restore patient failed:", err);
      await showAlert("Sorry — the patient could not be restored. Please try again.", {
        title: "Restore failed",
        tone: "danger",
      });
    } finally {
      setPendingId(null);
    }
  };

  // ✅ Load patients of selected study
  const handleStudySelect = async (studyId) => {
    setSelectedStudy(studyId);
    setHasAssembled(false);
    setPatients([]);
    if (!studyId) {
      setPatientsLoading(false);
      setAssembledLoading(false);
      return;
    }
    setPatientsLoading(true);
    setAssembledLoading(true);

    const q = query(collection(db, getSourcexPatientsPath()), where("studyId", "==", studyId));
    try {
      const snap = await getDocs(q);
      setPatients(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } finally {
      setPatientsLoading(false);
    }

    // Has "Generate source documents" been run for this study? If so, the built
    // per-visit template is openable directly, without going through the join.
    try {
      const asm = await getDoc(
        doc(db, getSourcexTemplatesPath(), `${studyId}__assembled`)
      );
      setHasAssembled(asm.exists());
    } catch (_) {
      setHasAssembled(false);
    } finally {
      setAssembledLoading(false);
    }
  };

  // ✅ Refresh list after adding
  const handlePatientAdded = () => handleStudySelect(selectedStudy);

  // Re-load the saved study's patients/templates on mount, so returning from
  // any SourceX subpage restores the full view and not just the selector label.
  useEffect(() => {
    const saved = readSavedStudy();
    if (saved) handleStudySelect(saved);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the remembered study in step with the selector.
  useEffect(() => {
    try {
      if (selectedStudy) sessionStorage.setItem(SELECTED_STUDY_KEY, selectedStudy);
      else sessionStorage.removeItem(SELECTED_STUDY_KEY);
    } catch {
      // Ignore — a lost selection is not worth breaking the page over.
    }
  }, [selectedStudy]);

  return (
    <div className="sourcex-page relative min-h-screen w-full text-slate-100 font-[Manrope] px-4 sm:px-6 py-6">
      <PageBackground />
      {/* Centered confirm / alert for this page. */}
      {dialog}

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-start gap-3 mb-6"
        >
          <div className="w-full flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs text-slate-300 hover:text-white transition"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
              <button
                onClick={() => navigate("/coordinator")}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs text-slate-300 hover:text-white transition"
              >
                <Home className="w-3.5 h-3.5" /> Coordinator
              </button>
            </div>

            {/* De-emphasized entry points — kept visible for the tech team but
                intentionally out of the main workflow. Only shown once a study
                is picked, since all three act on the selected study.
                No icons: three small grey buttons in a row read as a list of
                names, and a picture beside each one only made them wider.
                "Ask about Visits" joined them here rather than keeping a
                full-width card of its own in the middle of the page — it is
                parked in exactly the same sense the other two are. */}
            {selectedStudy && (
              <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
                <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-slate-600">
                  Advanced
                </span>
                <button
                  onClick={async () => {
                    // Already built: greyed out for staff; a site admin can override (recorded).
                    if (PROTOCOL_COMPARE_ENABLED && hasAssembled) {
                      if (!isSiteAdmin) return;
                      const ok = await askConfirm("Source documents for this study are already built. Edit those instead. Draft a new template with AI anyway?", {
                        title: "Source documents are already built",
                        confirmLabel: "Draft anyway (admin)",
                        tone: "danger",
                      });
                      if (!ok) return;
                      logProtocolEvent(selectedStudy, { type: "rebuild_override", detail: "SourceX home: Generate from Protocol (AI)" });
                    }
                    setGenOpen(true);
                  }}
                  disabled={PROTOCOL_COMPARE_ENABLED && hasAssembled && !isSiteAdmin}
                  title={PROTOCOL_COMPARE_ENABLED && hasAssembled ? "Source documents are already built, so edit those instead." : "Draft a template from the protocol with AI (not part of the current workflow)"}
                  className="inline-flex items-center px-2.5 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 hover:bg-white/[0.07] text-[11px] text-slate-400 hover:text-slate-200 transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white/[0.03]"
                >
                  Generate from Protocol (AI)
                </button>
                <button
                  onClick={() => navigate(`/sourcex/template/${selectedStudy}`)}
                  title="Open the manually built template for this study (not part of the current workflow)"
                  className="inline-flex items-center px-2.5 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 hover:bg-white/[0.07] text-[11px] text-slate-400 hover:text-slate-200 transition"
                >
                  Manual Template
                </button>
                {PROTOCOL_EXTRACTION_ENABLED && (
                  <SourceDocChat studyId={selectedStudy} />
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative shrink-0">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl blur-md opacity-70" />
              <div className="relative w-11 h-11 rounded-2xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold leading-none">
                SourceX
              </div>
              <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-white leading-tight truncate">
                Source Document Manager
              </h1>
            </div>
          </div>
        </motion.div>

        {/* Searchable study selector */}
        <div ref={studyRef} className="relative mb-6">
          <button
            onClick={() => setStudyOpen((v) => !v)}
            className="w-full flex items-center gap-2 pl-10 pr-9 py-3 rounded-2xl bg-white/[0.04] border border-white/10 text-sm text-left hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 transition backdrop-blur-xl relative"
          >
            <FlaskConical className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-300" />
            <span className={selectedStudy ? "text-slate-100" : "text-slate-500"}>
              {selectedStudy
                ? (() => {
                    const s = studies.find((x) => x.STUDY_ID === selectedStudy);
                    return s ? `(${s.STUDY_ID}) — ${s.NAME}` : selectedStudy;
                  })()
                : "Select Study"}
            </span>
            <ChevronDown
              className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 transition-transform ${
                studyOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {studyOpen && (
            <div className="absolute z-30 mt-1.5 left-0 right-0 rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_20px_60px_-15px_rgba(124,58,237,0.5)] overflow-hidden">
              <div className="sticky top-0 border-b border-white/10 bg-[#0c0826]/95">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    autoFocus
                    type="text"
                    placeholder="Search study by name or ID…"
                    value={studySearch}
                    onChange={(e) => setStudySearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
                  />
                </div>
              </div>
              <ul className="max-h-72 overflow-y-auto py-1">
                {studies
                  .filter((s) =>
                    `${s.NAME} ${s.STUDY_ID}`
                      .toLowerCase()
                      .includes(studySearch.toLowerCase())
                  )
                  .map((s) => (
                    <li
                      key={s.id}
                      onClick={() => {
                        handleStudySelect(s.STUDY_ID);
                        setStudyOpen(false);
                        setStudySearch("");
                      }}
                      className={`mx-1 px-3 py-2 rounded-lg cursor-pointer text-sm flex items-center gap-2 transition ${
                        selectedStudy === s.STUDY_ID
                          ? "bg-purple-500/15 text-white border border-purple-300/30"
                          : "text-slate-200 hover:bg-white/[0.05] border border-transparent"
                      }`}
                    >
                      <FlaskConical className="w-3.5 h-3.5 text-purple-300 shrink-0" />
                      <span className="truncate">
                        {s.NAME}{" "}
                        <span className="text-slate-500 font-mono">({s.STUDY_ID})</span>
                      </span>
                    </li>
                  ))}
                {studies.filter((s) =>
                  `${s.NAME} ${s.STUDY_ID}`
                    .toLowerCase()
                    .includes(studySearch.toLowerCase())
                ).length === 0 && (
                  <li className="px-3 py-3 text-sm text-slate-500 text-center">
                    No studies match.
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {selectedStudy && (
          <>
            {/* Workflow — the three ordered steps that build a study's source
                documents: extract the protocol, extract the blank eCRF, then
                map the two and create the documents. Laid out as numbered
                steps on one line. */}
            {(PROTOCOL_EXTRACTION_ENABLED ||
              ECRF_EXTRACTION_ENABLED ||
              JOIN_REVIEW_ENABLED) && (
              <div className="mb-5">
                {/* The payoff of the three steps — the built per-visit source
                    documents, and where new patients actually get enrolled
                    from. Shown FIRST and unmissable once it exists, so a
                    coordinator lands here rather than accidentally wandering
                    back into a rebuild step. Always ONE of three states —
                    checking / ready / not built yet — never just silently
                    absent, which used to look identical to "nothing here,
                    something's wrong" during the brief lookup right after
                    picking a study. */}
                {assembledLoading ? (
                  <div className="mb-4 w-full flex items-center gap-3 px-5 py-4 rounded-2xl border border-white/10 bg-white/[0.03]">
                    <span className="shrink-0 w-10 h-10 rounded-xl bg-white/[0.06] flex items-center justify-center">
                      <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                    </span>
                    <span className="text-sm text-slate-400">
                      Checking for built source documents…
                    </span>
                  </div>
                ) : hasAssembled ? (
                  <motion.button
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() =>
                      navigate(`/sourcex/template/${selectedStudy}__assembled`)
                    }
                    title="The per-visit source documents built from the Protocol → eCRF mapping"
                    // Louder than the steps below it by every means available —
                    // size, ring, shadow — because once the documents exist this
                    // is the ONLY thing on the page anyone should be clicking.
                    // The three build steps are still reachable (a rebuild is a
                    // real thing to want) but they are now the quiet option, and
                    // this has to win at a glance.
                    className="mb-4 w-full flex items-center gap-4 px-6 py-5 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 ring-2 ring-emerald-300/50 shadow-[0_14px_44px_-10px_rgba(16,185,129,0.75)] hover:shadow-[0_18px_56px_-8px_rgba(16,185,129,0.95)] hover:brightness-110 transition"
                  >
                    {/* An empty column the same width as the chevron opposite,
                        so "centred" is centred on the BUTTON rather than on the
                        space left beside the chevron. */}
                    <span aria-hidden="true" className="shrink-0 w-6" />
                    {/* Two short lines. The sentence that was here explained
                        where patients enrol from, which is true but is not what
                        someone glancing at this button needs — the one thing it
                        has to say is "this one". */}
                    <span className="min-w-0 flex-1 text-center">
                      <span className="block text-lg font-bold text-white leading-snug">
                        Review Built Source Documents
                      </span>
                      <span className="block text-xs font-medium text-emerald-50/90 mt-0.5">
                        This is where you want to be
                      </span>
                    </span>
                    <ChevronRight className="shrink-0 w-6 h-6 text-white" />
                  </motion.button>
                ) : (
                  <div className="mb-4 w-full flex items-center gap-3 px-5 py-4 rounded-2xl border border-dashed border-white/15 bg-white/[0.02]">
                    <span className="shrink-0 w-10 h-10 rounded-xl bg-white/[0.05] flex items-center justify-center">
                      <FileText className="w-5 h-5 text-slate-500" />
                    </span>
                    <span className="text-sm text-slate-400">
                      Source documents haven't been built yet — finish the steps below.
                    </span>
                  </div>
                )}

                <div className="mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                    {hasAssembled ? "Rebuild source documents" : "Build source documents"}
                  </div>
                  {/* Said in words, not left to the fade alone. Somebody who
                      has scrolled past the green card needs to know what these
                      three do NOW, which is not what they did the first time. */}
                  {hasAssembled && (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Already built — only run these again to rebuild from scratch.
                    </div>
                  )}
                </div>
                {/* The fade lives on each CARD, never on this grid. A
                    `hover:opacity-*` here fires for a pointer anywhere inside
                    the grid, so hovering one step lit all three and the
                    highlight said nothing about which one was under the cursor
                    — the opposite of what a hover state is for. */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Number, then title, and nothing else. These are three
                      ordered steps, so the ORDER is the thing to read first —
                      it was previously a 7px chip losing to an icon and two
                      lines of prose beside it. The subtitles said what each
                      page does, which every one of those pages says again at
                      the top of itself; here they only made three buttons look
                      like three paragraphs. Icons went with them: a picture of
                      a document next to the words "Upload Protocol" adds
                      nothing to a numbered list of three. */}
                  {[
                    {
                      enabled: PROTOCOL_EXTRACTION_ENABLED,
                      onClick: () => navigate(`/sourcex/protocols/${selectedStudy}`),
                      title: "Upload Protocol",
                    },
                    {
                      enabled: ECRF_EXTRACTION_ENABLED,
                      onClick: () => navigate(`/sourcex/ecrf/${selectedStudy}`),
                      title: "Upload Blank eCRF",
                    },
                    {
                      enabled: JOIN_REVIEW_ENABLED,
                      onClick: () => navigate(`/sourcex/join/${selectedStudy}`),
                      title: "Map & Create Documents",
                    },
                  ].map((step, i) => {
                    // Once the documents exist a step rests at 40%, so the green
                    // card above wins the page at a glance. The HOVER is the
                    // same either way, though: whichever card is under the
                    // cursor comes fully back and pops. What stops an accidental
                    // rebuild is the resting state and the line above the row,
                    // not a hover so timid you can't tell what you are pointing
                    // at.
                    const restCls = `border-purple-300/25 from-purple-500/20 to-blue-500/10 ${
                      hasAssembled ? "opacity-40" : ""
                    }`;
                    // A disabled card gets no hover treatment at all, which is
                    // also why none of this needs `disabled:hover:*` overrides
                    // to undo it — and why `hover:opacity-100` can never fight
                    // `disabled:opacity-40` for a greyed-out step.
                    const hoverCls = !step.enabled
                      ? ""
                      : `hover:z-10 hover:border-purple-300/70 hover:from-purple-500/45 hover:to-blue-500/25 hover:ring-2 hover:ring-purple-400/40 hover:shadow-[0_16px_44px_-12px_rgba(139,92,246,0.75)] ${
                          hasAssembled ? "hover:opacity-100" : ""
                        }`;
                    return (
                      <motion.button
                        key={step.title}
                        // Lifted AND enlarged, so the card under the cursor grows
                        // out of the row rather than just brightening in place.
                        // `hover:z-10` in hoverCls is what lets it overlap its
                        // neighbours instead of being clipped by the next grid
                        // cell.
                        whileHover={step.enabled ? { y: -4, scale: 1.04 } : undefined}
                        whileTap={step.enabled ? { scale: 0.98 } : undefined}
                        onClick={step.enabled ? step.onClick : undefined}
                        disabled={!step.enabled}
                        title={step.enabled ? undefined : "Not enabled"}
                        // Filled, like the green card, so a step reads as a
                        // button rather than an outline — but purple, and at a
                        // fraction of the saturation. These are the route you
                        // take once; the green card is the destination. Same
                        // family as the number chip they carry, so a card and
                        // its number read as one object. See restCls/hoverCls
                        // for how both change once the documents exist.
                        className={`group relative flex items-center gap-4 text-left px-5 py-6 rounded-2xl border bg-gradient-to-br transition duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${restCls} ${hoverCls}`}
                      >
                        <span className="shrink-0 w-12 h-12 rounded-2xl bg-purple-500/30 border border-purple-300/50 flex items-center justify-center text-2xl font-extrabold leading-none text-purple-50 tabular-nums">
                          {i + 1}
                        </span>
                        <span className="min-w-0 text-base font-semibold leading-snug text-white">
                          {step.title}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Ask about Visits used to sit here, as a full-width card between
                the build steps and the patient list. It moved up to the header's
                Advanced row — see there. */}

            {/* Enrolled patients */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white">
                  {showRemoved ? "Removed Patients" : "Enrolled Patients"}
                </h2>
                <div className="flex items-center gap-2">
                  {/* Removed patients stay visible to anyone signed in — the
                      record isn't gone, so hiding it from staff would only
                      make it look like it was. */}
                  {removedPatients.length > 0 && (
                    <button
                      onClick={() => setShowRemoved((v) => !v)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border transition ${
                        showRemoved
                          ? "bg-amber-400/20 border-amber-300/40 text-amber-100"
                          : "bg-white/[0.05] border-white/10 text-slate-300 hover:bg-white/[0.1]"
                      }`}
                    >
                      {showRemoved ? (
                        <>
                          <ArrowLeft className="w-3.5 h-3.5" /> Back to enrolled
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-3.5 h-3.5" /> Removed (
                          {removedPatients.length})
                        </>
                      )}
                    </button>
                  )}
                  {!showRemoved && (
                    <motion.button
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => setModalOpen(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-xs font-semibold text-white shadow hover:brightness-110 transition"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Patient
                    </motion.button>
                  )}
                </div>
              </div>

              {patientsLoading ? (
                <div className="px-4 py-8 flex items-center justify-center gap-2 text-sm text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading patients…
                </div>
              ) : listedPatients.length === 0 ? (
                <p className="px-4 py-8 text-sm text-slate-500 italic text-center">
                  {showRemoved
                    ? "No removed patients for this study."
                    : "No patients yet for this study."}
                </p>
              ) : (
                <div className="p-4">
                  {/* 3-column grid of patient cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {/* The card is a wrapper, not a button, because the delete
                        control is a button of its own and buttons can't nest. */}
                    {pagedPatients.map((p) => {
                      const removed = !!p.deletedAt;
                      return (
                        <div key={p.id} className="relative group">
                          <button
                            onClick={() => navigate(`/sourcex/patient/${p.id}`)}
                            disabled={pendingId === p.id}
                            className={`w-full text-left p-3 rounded-xl border transition flex items-center gap-3 disabled:opacity-50 ${
                              removed
                                ? "border-amber-400/25 bg-amber-400/[0.05] hover:bg-amber-400/[0.09]"
                                : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20"
                            }`}
                          >
                            <div
                              className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold text-white ring-1 ring-white/20 shrink-0 ${
                                removed
                                  ? "bg-slate-600/60 grayscale"
                                  : "bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500"
                              }`}
                            >
                              {(p.name || "?").charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p
                                className={`font-medium truncate transition ${
                                  removed
                                    ? "text-slate-300 line-through decoration-slate-500"
                                    : "text-white group-hover:text-purple-100"
                                }`}
                              >
                                {p.name || "Unnamed"}
                              </p>
                              <p className="text-xs text-slate-400 truncate">
                                DOB: {p.dob || "—"} · Random#: {p.randomization || "—"}
                              </p>
                              {removed ? (
                                <p className="text-xs text-amber-200/80 truncate">
                                  Removed {formatStamp(p.deletedAt)}
                                  {p.deletedBy ? ` by ${p.deletedBy}` : ""}
                                </p>
                              ) : (
                                <p className="text-xs text-slate-400 truncate">
                                  Screen#: {p.screening || "—"}
                                </p>
                              )}
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-slate-300 transition shrink-0" />
                          </button>
                          {removed ? (
                            <button
                              onClick={() => handleRestorePatient(p)}
                              disabled={pendingId === p.id}
                              title={`Restore ${p.name || "this patient"} to the enrolled list`}
                              aria-label={`Restore ${p.name || "this patient"}`}
                              className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 h-7 px-2 rounded-lg text-[11px] text-emerald-100 bg-emerald-500/20 border border-emerald-400/30 hover:bg-emerald-500/30 transition disabled:cursor-wait"
                            >
                              <Undo2 className="w-3 h-3" /> Restore
                            </button>
                          ) : (
                            <button
                              onClick={() => handleRemovePatient(p)}
                              disabled={pendingId === p.id}
                              title={`Remove ${p.name || "this patient"} from the list`}
                              aria-label={`Remove ${p.name || "this patient"} from the list`}
                              className="row-actions absolute top-1.5 right-1.5 w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-300 hover:bg-rose-500/20 transition disabled:cursor-wait"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Pager — only when the list spans more than one page */}
                  {patientPageCount > 1 && (
                    <div className="mt-4 flex items-center justify-between gap-3">
                      <span className="text-xs text-slate-400">
                        Showing {patientStart + 1}–{patientStart + pagedPatients.length}{" "}
                        of {patients.length}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setPatientPage((p) => Math.max(1, p - 1))}
                          disabled={safePatientPage === 1}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/[0.05] border border-white/10 text-xs text-slate-300 hover:bg-white/[0.1] transition disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="w-3 h-3" /> Prev
                        </button>
                        <span className="text-xs text-slate-400 px-1 tabular-nums">
                          Page {safePatientPage} of {patientPageCount}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setPatientPage((p) => Math.min(patientPageCount, p + 1))
                          }
                          disabled={safePatientPage === patientPageCount}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/[0.05] border border-white/10 text-xs text-slate-300 hover:bg-white/[0.1] transition disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Next <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {modalOpen && selectedStudy && (
        <AddPatientModal
          studyId={selectedStudy}
          onClose={() => setModalOpen(false)}
          onPatientAdded={handlePatientAdded}
        />
      )}

      {genOpen && selectedStudy && (
        <GenerateTemplateModal
          studyId={selectedStudy}
          studyName={studies.find((s) => s.STUDY_ID === selectedStudy)?.NAME || ""}
          onClose={() => setGenOpen(false)}
        />
      )}
    </div>
  );
}
